import { useEffect, useMemo, useState } from "react";
import { ApiError } from "@/api/fetchApi";
import { useFamily, usePlan, usePlanJob } from "@/api/queries";
import { Copy } from "@/copy/copy";
import { Spinner } from "@/components/Spinner";
import { PlanJob_State } from "@/generated/backend/v1/plan";
import { formatDate, formatMonthShort, monthsBetween, type PDate } from "@/plan-logic/lib/dates";
import { money, moneyShort } from "@/plan-logic/lib/format";
import { type ImpactCard, impactCards, simulatedSpan } from "@/plan-logic/plan-view";
import { PROVINCES } from "@/plan-logic/questionnaire/draft";
import { useOnbNav } from "../Screen";
import { useDraft } from "../useDraft";

const LOAD_LINES = (province: string) => [
  "Reading what you told us",
  `Modelling tax in ${province}`,
  "Testing thousands of ways to order your money",
  "Comparing that to changing nothing",
];

/**
 * "Working out your plan": polls the plan job queued by the save. With no job to poll (a draft left
 * on this step, in a new tab) or one the backend doesn't know for this user (404), `onStale`.
 */
export function BuildingStep({
  jobId,
  saveError,
  onRetry,
  onStale,
}: {
  jobId?: string;
  saveError?: string;
  onRetry: () => void;
  onStale: () => void;
}) {
  const { draft } = useDraft();
  const nav = useOnbNav();
  const job = usePlanJob(jobId);
  const plan = usePlan(!!jobId);
  const [ticks, setTicks] = useState(0);
  const province = PROVINCES.find(([p]) => p === draft.province)?.[1] ?? "your province";
  const lines = LOAD_LINES(province);

  useEffect(() => {
    const t = setInterval(() => setTicks((n) => Math.min(n + 1, lines.length - 1)), 900);
    return () => clearInterval(t);
  }, [lines.length]);

  const state = job.data?.state;
  const failed = state === PlanJob_State.FAILED;
  useEffect(() => {
    if (state === PlanJob_State.SUCCEEDED && plan.data?.plans?.abiPlan) nav.go("plan");
  }, [state, plan.data, nav]);
  const stale = !saveError && (!jobId || (job.error instanceof ApiError && job.error.isNotFound));
  useEffect(() => {
    if (stale) onStale();
  }, [stale, onStale]);

  if (saveError || failed) {
    return (
      <section className="screen on" data-testid="onb-building-failed">
        <div className="narrow">
          <h1>We couldn&rsquo;t finish your plan</h1>
          <p className="sub">
            {saveError ?? job.data?.errorMessage ?? "Something stopped Abi from finishing."} Your
            answers are saved on this device.
          </p>
          <div className="nav">
            <button type="button" className="btn g" onClick={() => nav.go("safety")}>
              Back
            </button>
            <button type="button" className="btn p sp" onClick={onRetry} data-testid="onb-retry">
              Try again
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="screen on" data-testid="onb-building">
      <div className="load">
        <div className="spin" />
        <Copy k="onboarding.building.h1" as="h1" style={{ fontSize: 24, marginBottom: 18 }} />
        <div className="loadlist" id="ll">
          {lines.map((l, i) => (
            <div key={l} className={i <= ticks ? "on" : ""}>
              <span className="tick">&#10003;</span>
              {l}
            </div>
          ))}
        </div>
        <p className="hint" style={{ marginTop: 18 }}>
          This usually takes under a minute.
        </p>
      </div>
    </section>
  );
}

const pct = (f: number) => `${Math.round(Math.min(1, f) * 100)}% funded`;

/** A move on a priority's funding, as a share of the goal itself (v44): "Fully paid for", … */
export function fundingMove(abi: number, naive: number): string {
  const d = Math.round((Math.min(1, abi) - Math.min(1, naive)) * 100);
  if (d === 0) return "No change";
  if (abi >= 0.995 && d > 0) return "Fully paid for";
  return `${Math.abs(d)}% ${d > 0 ? "more" : "less"} of it paid for`;
}

type CardText = {
  cap: string;
  was?: string;
  is: string;
  diff?: string;
  tail?: string;
  bad?: boolean;
};

/** A card's words and figures, from plan-view's numbers (no figure here is computed, only shown). */
export function cardText(c: ImpactCard): CardText {
  const delta = (a: number, n: number | undefined, more: string, less: string) =>
    n === undefined
      ? undefined
      : Math.abs(a - n) < 1
        ? "No change"
        : `${money(Math.abs(a - n))} ${a > n ? more : less}`;
  switch (c.kind) {
    case "retirement":
      return {
        cap: "Retirement",
        was: c.naive === undefined ? undefined : moneyShort(c.naive),
        is: moneyShort(c.abi),
        diff: delta(c.abi, c.naive, "more", "less"),
        tail: c.atIsRetirement
          ? "by the year you stop working"
          : `by ${c.at?.year ?? "the end of the plan"}`,
        bad: c.naive !== undefined && c.abi < c.naive - 1,
      };
    case "monthly":
      return {
        cap: "What you keep each month",
        was: c.naive === undefined ? undefined : `${money(c.naive)} a month`,
        is: `${money(c.abi)} a month`,
        diff: delta(c.abi, c.naive, "a month more", "a month less"),
        // as Cash Flow's headline: "without earning a dollar extra" only when it's more
        tail:
          c.naive !== undefined && c.abi < c.naive - 1
            ? "on the same income"
            : "without earning a dollar extra",
        bad: c.naive !== undefined && c.abi < c.naive - 1,
      };
    case "interest":
      return {
        cap: "Interest on your debt",
        was: c.naive === undefined ? undefined : money(c.naive),
        is: money(c.abi),
        diff: delta(c.naive ?? c.abi, c.naive === undefined ? undefined : c.abi, "saved", "more"),
        tail: "over the life of what you owe",
        bad: c.naive !== undefined && c.abi > c.naive + 1,
      };
    case "debtFree": {
      const when = (d?: PDate) => (d ? formatMonthShort(d) : `Not by ${c.end?.year ?? "the end"}`);
      const m = c.naive && c.abi ? monthsBetween(c.abi, c.naive) : undefined;
      return {
        cap: "Debt free",
        was: c.naive === undefined && !c.end ? undefined : when(c.naive),
        is: when(c.abi),
        diff:
          m === undefined
            ? c.abi && !c.naive
              ? "Cleared"
              : undefined
            : m === 0
              ? "The same month"
              : `${Math.abs(m)} month${Math.abs(m) === 1 ? "" : "s"} ${m > 0 ? "sooner" : "later"}`,
        tail:
          m === undefined
            ? c.abi && !c.naive
              ? "where carrying on as you are doesn't"
              : undefined
            : m === 0
              ? "as carrying on as you are"
              : "than carrying on as you are",
        bad: m !== undefined && m < 0,
      };
    }
    case "priority":
      return {
        cap: c.name,
        was: pct(c.naive),
        is: pct(c.abi),
        diff: fundingMove(c.abi, c.naive),
        tail: "by the date you set for it",
      };
  }
}

/**
 * "Abi's impact" (v44–v46): the title, what Abi did (the days are counted from the plan; the number of
 * branches it tested isn't claimed until the engine reports it), and four to six before-and-after
 * cards, two to a row, from the plan's two runs (`impactCards`). No chart. No plan (404: a draft left
 * on this step after the data was wiped, or someone else's): `onStale`.
 */
export function ImpactStep({ onEnter, onStale }: { onEnter: () => void; onStale: () => void }) {
  const plan = usePlan();
  const noPlan = plan.data === null;
  useEffect(() => {
    if (noPlan) onStale();
  }, [noPlan, onStale]);
  const abi = plan.data?.plans?.abiPlan;
  const naive = plan.data?.plans?.naivePlan;
  const fam = useFamily().data?.family;
  const cards = useMemo(() => (abi ? impactCards(fam, abi, naive) : null), [fam, abi, naive]);

  if (plan.isPending || !abi || !cards) return <Spinner label="Loading your plan" />;
  const span = simulatedSpan(abi);

  return (
    <section className="screen on" data-testid="onb-plan">
      <div className="hero" style={{ paddingBottom: 4 }}>
        <h1 className="impacth">Abi&rsquo;s impact</h1>
        <p className="lead" style={{ margin: "12px auto 0", maxWidth: "62ch" }} id="headsub">
          {span.days ? (
            <>
              Abi simulated your money day by day out to {span.endYear} &mdash;{" "}
              <b data-testid="impact-days">{span.days.toLocaleString("en-CA")} days</b> of it
              &mdash; then ran the whole thing again for every big decision it could have made
              differently, and against changing nothing at all. This is the plan that came out on
              top.
            </>
          ) : (
            "Abi simulated your money day by day, then compared its plan against changing nothing at all."
          )}
        </p>
      </div>

      <div className="ba" id="ba" data-testid="impact-cards">
        {cards.map((c) => {
          const t = cardText(c);
          return (
            <div
              className="bac"
              key={c.kind === "priority" ? c.key : c.kind}
              data-testid={`impact-${c.kind}`}
            >
              <div className="cap">{t.cap}</div>
              <div className="barow">
                {t.was !== undefined && (
                  <div className="bacol">
                    <span className="lab">Your current trajectory</span>
                    <span className="was">{t.was}</span>
                  </div>
                )}
                <div className="bacol">
                  <span className="lab">With Abi&rsquo;s plan</span>
                  <span className="is">{t.is}</span>
                </div>
              </div>
              {t.diff && (
                <p className="foot">
                  <b className={t.bad ? "worse" : undefined}>{t.diff}</b> {t.tail}
                </p>
              )}
            </div>
          );
        })}
      </div>

      <div className="assume" style={{ marginTop: 22 }}>
        <span>i</span>
        <span>
          {abi.meta?.assumptions
            ? `Investments here grow at ${+abi.meta.assumptions.investmentReturnRate.toFixed(2)}% a year and prices at ${+abi.meta.assumptions.inflationRate.toFixed(2)}%. `
            : ""}
          Real markets don&rsquo;t move in a straight line, and this projection doesn&rsquo;t try to
          show that &mdash; it shows the middle of the range, not the range. These figures assume
          you follow the plan. Plan as of {formatDate(abi.meta?.startDate)}.
        </span>
      </div>

      <div className="nav" style={{ justifyContent: "center", flexDirection: "column", gap: 12 }}>
        <button
          type="button"
          className="btn p big-cta"
          onClick={onEnter}
          data-testid="onb-enter-app"
        >
          Go to my dashboard
        </button>
        <p
          style={{
            textAlign: "center",
            fontSize: 13.5,
            color: "var(--ink-3)",
            maxWidth: "46ch",
            margin: 0,
          }}
        >
          In your dashboard you&rsquo;ll find your recommended actions, and the handful of things
          that would make these numbers sharper.
        </p>
      </div>
    </section>
  );
}
